import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import DrawerContent from "./DrawerContent";
import DrawerHeader from "./DrawerHeader";
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from "config";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import useZustandStore from "zustandStore";
import mainStyle from "./drawer.module.scss";

export function useZoomWatcher() {
  const getZoomInfo = () => {
    const screenWidth = window.screen.width;
    const visualViewportWidth = window.innerWidth;
    const innerHeight = window.innerHeight;

    const zoomRatio = screenWidth / visualViewportWidth;

    let shouldUseScroll = false;

    if (innerHeight < 750) {
      shouldUseScroll = true;
    } else if (innerHeight < 800) {
      shouldUseScroll = zoomRatio > 1.1;
    } else if (innerHeight < 1000) {
      shouldUseScroll = zoomRatio > 1.3;
    } else {
      shouldUseScroll = zoomRatio > 1.5;
    }

    return {
      zoomRatio,
      innerWidth: window.innerWidth,
      innerHeight: innerHeight,
      shouldUseScroll,
    };
  };

  const [zoomInfo, setZoomInfo] = useState(getZoomInfo());

  useEffect(() => {
    const handleResize = () => {
      setZoomInfo(getZoomInfo());
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  return zoomInfo;
}

export default function ResponsiveDrawer(props) {
  const { window: propsWindow } = props;
  const { menuMaster } = useGetMenuMaster();
  const { isNavDisabled } = useZustandStore();
  const isUnityRd3 = window.location.hostname.includes('unity-rd3');
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(
    () => <DrawerHeader open={drawerOpen} toggleDrawer={handlerDrawerOpen} />,
    [drawerOpen]
  );

  const container =
    propsWindow !== undefined ? () => propsWindow().document.body : undefined;

  const { shouldUseScroll } = useZoomWatcher();

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        component="nav"
        sx={{
          width: MINI_DRAWER_WIDTH,
          flexShrink: 0,
          ...(isUnityRd3 && isNavDisabled && {
            pointerEvents: "none",
            opacity: 0.6,
          }),
        }}
        aria-label="mailbox folders"
      >
        {/* 這個 drawer 是顯示 drawer content */}
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              backgroundColor: 'primary.main',
              zIndex: 1300,
              top: 0,
              height: "100vh",
            },
          }}
        >
          {drawerHeader}
          <Box
            className={shouldUseScroll ? mainStyle["custom-scrollbar"] : ""}
            sx={{
              flex: 1,
              height: "100vh",
              overflowY: shouldUseScroll ? "auto" : "hidden",
              overflowX: "hidden",
              minHeight: 0,
            }}
          >
            {drawerContent}
          </Box>
        </Drawer>
        {/* 這個 drawer 是固定顯示在左側只有 drawer icon */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: MINI_DRAWER_WIDTH,
              overflow: "hidden",
              backgroundColor: 'primary.main',
              top: 0,
              height: "100vh",
            },
          }}
          open={drawerOpen}
        >
          {drawerHeader}
          <Box
            className={shouldUseScroll ? mainStyle["custom-scrollbar"] : ""}
            sx={{
              flex: 1,
              height: "100vh",
              overflowY: shouldUseScroll ? "auto" : "hidden",
              overflowX: "hidden",
              minHeight: 0,
            }}
          >
            {drawerContent}
          </Box>
        </Drawer>
      </Box>
    </Box>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};
